import React, { useState, useContext } from 'react';
import { Trash2 } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { GAME_TYPES } from '../../utils/constants';
import * as firestoreService from '../../services/firestoreService';

const ScorecardDetailView = ({ game, onBack, onDelete }) => {
  const { currentUser, showConfirm } = useContext(AppContext);
  const [weather, setWeather] = useState('sunny');
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  const gameTypeInfo = GAME_TYPES.find(g => g.id === game.gameType);
  const holes = game.holes || {};
  const holeNumbers = Object.keys(holes).map(Number).sort((a, b) => a - b);
  const userId = currentUser.id;
  const totalScore = holeNumbers.reduce((sum, n) => sum + (holes[String(n)]?.scores?.[userId] || 0), 0);
  const totalPar = holeNumbers.reduce((sum, n) => sum + (holes[String(n)]?.par || 4), 0);
  const diff = totalScore - totalPar;
  const isEvent = !!game.eventId;
  const dateStr = game.completedAt ? new Date(game.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const getBestHole = () => {
    let best = null;
    holeNumbers.forEach(n => {
      const h = holes[String(n)] || {};
      const score = h.scores?.[userId] || 0;
      if (score > 0) {
        const holeDiff = score - (h.par || 4);
        if (best === null || holeDiff < best.diff) {
          best = { hole: n, score, par: h.par || 4, diff: holeDiff };
        }
      }
    });
    return best;
  };

  const handleShare = async () => {
    if (!await showConfirm('Share your stats to the feed?', 'Share Stats', 'Share')) return;
    setSharing(true);
    try {
      const bestHole = getBestHole();
      await firestoreService.createPost({
        userId: currentUser.id,
        type: 'gameRecap',
        content: `My ${game.courseName} Stats! ${dateStr}`,
        gameRecap: {
          gameType: game.gameType,
          courseName: game.courseName,
          players: [{ userId: currentUser.id, name: currentUser.name }],
          finalScores: { [currentUser.id]: totalScore },
          totalPar,
          totalHoles: game.totalHoles || holeNumbers.length,
          myScore: totalScore,
          myDiff: diff,
          bestHole,
          isIndividual: true,
          weather,
        },
      });
      setSharing(false);
      setShared(true);
    } catch (err) {
      console.error('Error sharing scorecard:', err);
      setSharing(false);
    }
  };

  const canDelete = game.completedAt
    ? (Date.now() - new Date(game.completedAt).getTime()) < 24 * 60 * 60 * 1000
    : true;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button onClick={onBack} className="flex items-center space-x-1 text-gray-400 hover:text-white mb-4">
        <span className="text-lg leading-none">←</span><span>Back</span>
      </button>
      <div className="mb-4">
        <h2 className="text-xl font-bold text-white">{game.courseName}</h2>
        <p className="text-gray-400 text-sm">{dateStr} · {gameTypeInfo?.emoji} {gameTypeInfo?.name || game.gameType} · {game.totalHoles || holeNumbers.length} holes{isEvent ? ' · Event' : ''}</p>
      </div>

      {/* Score summary */}
      <div className="bg-gray-900 rounded-lg p-4 mb-4 flex justify-around text-center">
        <div>
          <p className="text-gray-400 text-xs mb-1">Score</p>
          <p className="text-3xl font-bold text-white">{totalScore || '—'}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs mb-1">Par</p>
          <p className="text-3xl font-bold text-white">{totalPar}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs mb-1">vs Par</p>
          <p className={`text-3xl font-bold ${diff <= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {totalScore > 0 ? (diff >= 0 ? `+${diff}` : `${diff}`) : '—'}
          </p>
        </div>
      </div>

      {/* Course info */}
      {game.selectedCourseData && (
        <div className="bg-gray-900 rounded-lg p-3 mb-4 text-sm text-gray-400 flex space-x-4">
          {game.selectedCourseData.slopeRating && <span>Slope {game.selectedCourseData.slopeRating}</span>}
          {game.selectedCourseData.courseRating && <span>Rating {game.selectedCourseData.courseRating}</span>}
          {game.selectedCourseData.parTotal && <span>Course Par {game.selectedCourseData.parTotal}</span>}
        </div>
      )}

      {/* Hole-by-hole table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-400 border-b border-gray-800">
              <th className="py-2 text-left font-medium">Hole</th>
              <th className="py-2 text-center font-medium">Par</th>
              <th className="py-2 text-center font-medium">Score</th>
              <th className="py-2 text-center font-medium">+/-</th>
            </tr>
          </thead>
          <tbody>
            {holeNumbers.map(n => {
              const h = holes[String(n)] || {};
              const score = h.scores?.[userId] || 0;
              const par = h.par || 4;
              const holeDiff = score - par;
              return (
                <tr key={n} className="border-b border-gray-900">
                  <td className="py-2 text-white font-medium">{n}</td>
                  <td className="py-2 text-center text-gray-400">{par}</td>
                  <td className="py-2 text-center text-white font-bold">{score || '—'}</td>
                  <td className={`py-2 text-center font-bold ${score === 0 ? 'text-gray-500' : holeDiff < 0 ? 'text-green-400' : holeDiff === 0 ? 'text-gray-400' : 'text-red-400'}`}>
                    {score > 0 ? (holeDiff >= 0 ? `+${holeDiff}` : `${holeDiff}`) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-700">
              <td className="py-2 text-white font-bold">Total</td>
              <td className="py-2 text-center text-gray-400 font-bold">{totalPar}</td>
              <td className="py-2 text-center text-white font-bold">{totalScore || '—'}</td>
              <td className={`py-2 text-center font-bold ${diff <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {totalScore > 0 ? (diff >= 0 ? `+${diff}` : `${diff}`) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Share section */}
      {!shared && (
        <div className="mb-4">
          <p className="text-sm font-medium text-white mb-2">Weather conditions</p>
          <div className="flex space-x-2 mb-3">
            {[{ value: 'sunny', label: '☀️ Sunny' }, { value: 'cloudy', label: '☁️ Cloudy' }, { value: 'rainy', label: '🌧️ Rainy' }, { value: 'windy', label: '💨 Windy' }].map(opt => (
              <button
                key={opt.value}
                onClick={() => setWeather(opt.value)}
                className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${weather === opt.value ? 'bg-[#16a34a] border-[#16a34a] text-pure-white' : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleShare}
            disabled={sharing || totalScore === 0}
            className="w-full py-3 bg-blue-600 text-pure-white rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50"
          >
            {sharing ? 'Sharing...' : 'Share My Stats'}
          </button>
        </div>
      )}
      {shared && (
        <div className="mb-4 py-3 bg-gray-800 text-gray-300 rounded-lg text-center font-bold text-sm">
          Stats shared to feed
        </div>
      )}

      {/* Delete */}
      {canDelete && onDelete && (
        <button
          onClick={() => onDelete(game)}
          className="w-full flex items-center justify-center space-x-2 py-2 text-red-400 hover:text-red-300 text-sm"
        >
          <Trash2 className="w-4 h-4" />
          <span>Delete this scorecard</span>
        </button>
      )}
    </div>
  );
};

export default ScorecardDetailView;
